import { normalizeInputFields } from "./checklistInputFields";
import {
  CHECKLIST_RESPONSE_ACTION_KEYS,
  normalizeActionKeys,
} from "./checklistResponseConfig";

/** Strip optional ```json ... ``` wrapper from pasted text. */
export function stripJsonCodeFence(text) {
  let s = String(text || "").trim();
  if (!s) return s;
  const whole = /^```(?:json)?\s*\r?\n?([\s\S]*?)\r?\n?```\s*$/i;
  const mWhole = s.match(whole);
  if (mWhole) return mWhole[1].trim();
  const inner = /```(?:json)?\s*\r?\n?([\s\S]*?)\r?\n?```/i;
  const mInner = s.match(inner);
  if (mInner) return mInner[1].trim();
  return s;
}

function normalizeResponseActions(raw) {
  if (raw == null) return null;
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const allowed = new Set(CHECKLIST_RESPONSE_ACTION_KEYS);
  const filtered = [...new Set(raw.map((k) => String(k)).filter((k) => allowed.has(k)))];
  return filtered.length ? filtered : null;
}

function normalizeChecklistTypeItem(raw, index) {
  if (!raw || typeof raw !== "object") return null;
  const title = String(raw.title || raw.name || "").trim();
  if (!title) return null;

  const guidance = String(raw.guidance || raw.description || "").trim();
  const input_fields = normalizeInputFields(raw.input_fields);

  return {
    title,
    guidance,
    description: guidance,
    documents_required: Boolean(raw.documents_required),
    response_actions: normalizeResponseActions(raw.response_actions),
    input_fields,
    row_order: index,
    ...(raw.id != null && String(raw.id).trim() !== "" && String(raw.id) !== "0"
      ? { id: raw.id }
      : {}),
  };
}

/**
 * Parse pasted string into checklist type shape (+ optional title/description/actions).
 * Accepts: raw items array, { items }, or full type shape.
 */
export function parseImportedChecklistTypeJson(text) {
  const trimmed = stripJsonCodeFence(text);
  if (!trimmed) {
    const err = new Error("empty");
    err.code = "empty";
    throw err;
  }

  let data;
  try {
    data = JSON.parse(trimmed);
  } catch {
    const err = new Error("invalid_json");
    err.code = "invalid_json";
    throw err;
  }

  let arr;
  let title;
  let description;
  let default_response_actions;

  if (Array.isArray(data)) {
    arr = data;
  } else if (data && Array.isArray(data.items)) {
    arr = data.items;
    title = data.title;
    description = data.description;
    default_response_actions = data.default_response_actions;
  } else {
    const err = new Error("no_items_array");
    err.code = "no_items_array";
    throw err;
  }

  const items = arr
    .map((row, index) => normalizeChecklistTypeItem(row, index))
    .filter(Boolean);

  if (items.length === 0) {
    const err = new Error("no_valid_items");
    err.code = "no_valid_items";
    throw err;
  }

  return {
    items,
    title: title != null ? String(title).trim() : undefined,
    description: description != null ? String(description).trim() : undefined,
    default_response_actions:
      default_response_actions != null
        ? normalizeActionKeys(default_response_actions)
        : undefined,
  };
}

export function serializeChecklistTypeForExport(values = {}) {
  const payload = {
    title: values.title || "",
    description: values.description || "",
    default_response_actions: normalizeActionKeys(values.default_response_actions),
    items: (values.items || []).map((row, index) => ({
      title: row.title || "",
      guidance: row.guidance || row.description || "",
      documents_required: Boolean(row.documents_required),
      response_actions: row.response_actions ?? null,
      input_fields: normalizeInputFields(row.input_fields),
      row_order: row.row_order ?? index,
    })),
  };
  return JSON.stringify(payload, null, 2);
}

/** Minimal example for "insert sample" in the UI */
export const SAMPLE_CHECKLIST_TYPE_JSON = `{
  "title": "Vernerunde – byggeplass",
  "description": "Ukentlig sikkerhetsgjennomgang før arbeid starter",
  "default_response_actions": ["performed", "approved", "na", "deviation"],
  "items": [
    {
      "title": "Adkomst og avsperring",
      "guidance": "Kontroller at adkomstveier er ryddige og at farlige soner er tydelig avsperret.",
      "documents_required": false
    },
    {
      "title": "Personlig verneutstyr",
      "guidance": "Bekreft at alle på stedet bruker påkrevd PPE for aktiviteten.",
      "documents_required": false,
      "response_actions": ["approved", "deviation"]
    },
    {
      "title": "Målt luftkvalitet",
      "guidance": "Mål og dokumenter luftkvalitet der det kreves.",
      "documents_required": true,
      "input_fields": [
        {
          "label": "CO₂ (ppm)",
          "input_type": "number",
          "required": true,
          "unit": "ppm"
        },
        {
          "label": "Kommentar",
          "input_type": "textarea",
          "required": false
        }
      ]
    }
  ]
}`;
